import { VictronImage } from '@components/display/images';
import { APP_PREFIX_PATH } from '@constants/common';
import useExtensions from '@hooks/useExtensions';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { Box, IconButton, Skeleton, Stack, Typography } from '@mui/material';
import { useGetPublicNewsDetailQuery, useGetPublicNewsQuery, useLazyGetPublicNewsQuery } from '@news/services';
import { NewsItem } from '@news/types';
import { NotFound } from '@routes/NotFound';
import { useAppDispatch } from '@hooks/generic';
import { setBreadcrumbs } from '@store/breadcrumbSlice';
import clsx from 'clsx';
import { format } from 'date-fns';
import { RichTextReadOnly } from 'mui-tiptap';
import { useEffect, useLayoutEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Link, useParams } from 'react-router-dom';

const DISPLAYED_RECENT_NEWS_COUNT = 5;

const RecentNews = ({
    loading,
    topBorder,
    bottomBorder,
    ...news
}: NewsItem & { loading: boolean; topBorder?: boolean; bottomBorder?: boolean }) => {
    const rowClassName = clsx(
        'flex items-center pb-[10px] px-[7px] pt-[7px] bg-neutral-100 group gap-[10px] transition-colors',
        {
            'rounded-t-lg': topBorder,
            'rounded-b-lg': bottomBorder,
            'hover:bg-[#F1F6FD]': !loading,
        }
    );

    const rowContent = (
        <>
            {loading ? (
                <Skeleton variant="rounded" width={75} height={50} className="flex-shrink-0" />
            ) : (
                <VictronImage
                    src={news.picture}
                    alt={news.title}
                    width={75}
                    className="rounded-lg h-[50px] w-[75px] min-w-[75px] max-w-[75px] object-cover flex-shrink-0"
                />
            )}
            <Box width="100%">
                {loading ? (
                    <Skeleton variant="text" width={75} height={20} className="mb-1" />
                ) : (
                    <Typography color="rgba(0,0,0,0.40)" fontWeight={500} fontSize={11} className="line-clamp-1">
                        {format(news.date ?? new Date(), 'dd MMM yyyy')}
                    </Typography>
                )}
                {loading ? (
                    <Skeleton variant="text" width="100%" height={20} />
                ) : (
                    <Typography fontFamily="museo-sans" fontSize={14} fontWeight={500} className="line-clamp-2">
                        {news.title}
                    </Typography>
                )}
            </Box>
            <IconButton
                color="primary"
                className={clsx('opacity-0 transition-opacity', {
                    'group-hover:opacity-100': !loading,
                })}
            >
                <ArrowForwardIcon />
            </IconButton>
        </>
    );

    if (loading) {
        return <Box className={rowClassName}>{rowContent}</Box>;
    }

    return (
        <Link key={news.id} to={APP_PREFIX_PATH + `/news/${news.id}`} className={rowClassName}>
            {rowContent}
        </Link>
    );
};

export const NewsDetail = () => {
    const id = useParams<{ id: string }>().id;
    const {
        data,
        isLoading: isLoadingDetail,
        error: errorDetail,
    } = useGetPublicNewsDetailQuery(
        { id: Number(id) },
        {
            skip: isNaN(Number(id)),
        }
    );
    const { data: recentNews, isLoading: isRecentNewsLoading } = useGetPublicNewsQuery({
        page_size: DISPLAYED_RECENT_NEWS_COUNT,
    });
    const [parsedRecentNews, setParsedRecentNews] = useState<NewsItem[]>([]);
    const [isParsing, setIsParsing] = useState(true);
    const [getNews] = useLazyGetPublicNewsQuery();

    const dispatch = useAppDispatch();
    const extensions = useExtensions();
    const intl = useIntl();

    useLayoutEffect(() => {
        if (data) {
            dispatch(
                setBreadcrumbs([
                    { label: intl.formatMessage({ id: 'news', defaultMessage: 'News' }), to: APP_PREFIX_PATH + '/news' },
                    { label: data.title ?? '', to: APP_PREFIX_PATH + `/news/${data.id}`, loading: false },
                ])
            );
            document.title = `Victron Professional - ${data.title}`;
        } else if (errorDetail || isNaN(Number(id))) {
            dispatch(
                setBreadcrumbs([
                    { label: intl.formatMessage({ id: 'news', defaultMessage: 'News' }), to: APP_PREFIX_PATH + '/news' },
                    {
                        label: intl.formatMessage({ id: 'notFound', defaultMessage: 'Not Found' }),
                        to: APP_PREFIX_PATH + '/news',
                    },
                ])
            );
        } else {
            dispatch(
                setBreadcrumbs([
                    { label: intl.formatMessage({ id: 'news', defaultMessage: 'News' }), to: APP_PREFIX_PATH + '/news' },
                    {
                        label: intl.formatMessage({ id: 'loading', defaultMessage: 'Loading' }),
                        to: APP_PREFIX_PATH + `/news/${id}`,
                        loading: true,
                    },
                ])
            );
        }

        return () => {
            dispatch(
                setBreadcrumbs([
                    { label: intl.formatMessage({ id: 'news', defaultMessage: 'News' }), to: APP_PREFIX_PATH + '/news' },
                ])
            );
        };
    }, [data, dispatch, errorDetail, id, intl]);

    useEffect(() => {
        const parseRecentNews = async () => {
            try {
                const newsExcludingCurrent = recentNews?.results.filter((item) => item.id !== Number(id)) || [];
                if (newsExcludingCurrent.length < DISPLAYED_RECENT_NEWS_COUNT) {
                    const res = await getNews({ page_size: 1, page: DISPLAYED_RECENT_NEWS_COUNT + 1 }).unwrap();
                    const replacementNews = res.results ? [res.results?.[0]] : [];
                    setParsedRecentNews(() => [...newsExcludingCurrent, ...replacementNews]);
                } else {
                    setParsedRecentNews(newsExcludingCurrent);
                }
            } catch (error) {
                console.error('Error fetching recent news:', error);
            } finally {
                setIsParsing(false);
            }
        };
        if (Number(recentNews?.count) > 0 && !isRecentNewsLoading) {
            parseRecentNews();
        } else if (Number(recentNews?.count) === 0 && !isRecentNewsLoading) {
            setParsedRecentNews([]);
            setIsParsing(false);
        }
    }, [recentNews, getNews, id, isRecentNewsLoading]);

    const showRecentNews = isRecentNewsLoading || parsedRecentNews.length > 0 || isParsing;

    return !errorDetail && !isNaN(Number(id)) ? (
        <>
            <title>
                {intl.formatMessage(
                    {
                        id: 'news.detail.browserTitle',
                        defaultMessage: '{brand} - News Detail',
                    },
                    { brand: 'Victron Professional' }
                )}
            </title>
            <Stack p={{ xs: 2, md: 4 }} direction={{ xs: 'column', md: 'row' }} spacing="17px">
                <Box
                    borderRadius="8px"
                    border={1}
                    borderColor="rgba(0,0,0,0.16)"
                    bgcolor="white.main"
                    width={{ xs: '100%', md: showRecentNews ? '60%' : '100%' }}
                >
                    {isLoadingDetail ? (
                        <Skeleton variant="rounded" height={380} />
                    ) : (
                        <Box position="relative">
                            <VictronImage
                                src={data?.picture}
                                alt={data?.title}
                                width="100%"
                                className="rounded-lg h-[380px] object-cover"
                            />
                            {!data?.published && (
                                <Box
                                    borderRadius="24px"
                                    position="absolute"
                                    top={10}
                                    left={10}
                                    bgcolor="signal-warning.main"
                                    px="12px"
                                    py="4px"
                                >
                                    <Typography fontSize={16} fontWeight={500} color="white">
                                        <FormattedMessage id="common.status.draft" defaultMessage="Draft" />
                                    </Typography>
                                </Box>
                            )}
                        </Box>
                    )}
                    <Box p={2}>
                        {isLoadingDetail ? (
                            <>
                                <Skeleton variant="text">
                                    <Typography fontFamily="museo-sans" fontSize={32} fontWeight={600}>
                                        Loading title...
                                    </Typography>
                                </Skeleton>
                                <Skeleton variant="text" width={200}>
                                    <Typography fontSize={16} fontWeight={500} color="rgba(0,0,0,0.4)" mb={2}>
                                        Loading date...
                                    </Typography>
                                </Skeleton>
                                <Skeleton variant="rectangular" height={200} />
                            </>
                        ) : (
                            <>
                                <Typography fontFamily="museo-sans" fontSize={32} fontWeight={600}>
                                    {data?.title}
                                </Typography>
                                <Typography fontSize={16} fontWeight={500} color="rgba(0,0,0,0.4)" mb={2}>
                                    {format(data?.date ?? new Date(), 'dd MMM yyyy')}
                                </Typography>
                                <RichTextReadOnly content={data?.description} extensions={extensions} />
                            </>
                        )}
                    </Box>
                </Box>
                {showRecentNews && (
                    <Box
                        borderRadius="8px"
                        border={1}
                        borderColor="rgba(0,0,0,0.16)"
                        bgcolor="white.main"
                        width={{ xs: '100%', md: '40%' }}
                        height="fit-content"
                        p={2}
                    >
                        <Typography fontFamily="museo-sans" fontSize={16} letterSpacing={-0.621}>
                            <FormattedMessage id="news.recent" defaultMessage="Recent News" />
                        </Typography>
                        <Stack
                            direction="column"
                            mt={2}
                            divider={<Box borderBottom={1} borderColor="rgba(0,0,0,0.16)" />}
                            borderRadius="8px"
                        >
                            {isRecentNewsLoading || isParsing
                                ? Array.from({ length: DISPLAYED_RECENT_NEWS_COUNT }).map((_, index) => (
                                      <RecentNews
                                          key={`recent-news-skeleton-${index}`}
                                          id={index}
                                          loading
                                          topBorder={index === 0}
                                          bottomBorder={index === DISPLAYED_RECENT_NEWS_COUNT - 1}
                                      />
                                  ))
                                : parsedRecentNews.map((news, index) => (
                                      <RecentNews
                                          key={news.id}
                                          loading={false}
                                          {...news}
                                          topBorder={index === 0}
                                          bottomBorder={index === parsedRecentNews.length - 1}
                                      />
                                  ))}
                        </Stack>
                    </Box>
                )}
            </Stack>
        </>
    ) : (
        <NotFound />
    );
};
